import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';
import { ListenButton } from '../../common/AudioGuide';
import HelpWithVideoTour from '../../Help/HelpWithVideoTour';

const ACCENT = '#0f766e';
const API = (job) => `/p6-msp-export/${encodeURIComponent(job)}`;
const CONVERT_CFG = { timeout: 180000 };

const btn = {
  padding: '7px 12px',
  borderRadius: 8,
  border: '1px solid #cbd5e1',
  background: '#fff',
  cursor: 'pointer',
  fontSize: 13,
  color: '#334155',
};
const btnPrimary = { ...btn, background: ACCENT, borderColor: ACCENT, color: '#fff', fontWeight: 600 };
const btnGhost = { ...btn, background: 'transparent' };

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function filenameFromHeader(headers, fallback) {
  const raw = headers?.['content-disposition'] || headers?.['Content-Disposition'] || '';
  const star = raw.match(/filename\*=UTF-8''([^;]+)/i);
  if (star) {
    try { return decodeURIComponent(star[1]); } catch { /* keep looking */ }
  }
  const quoted = raw.match(/filename="([^"]+)"/i);
  return quoted ? quoted[1] : fallback;
}

const MspExport = ({ jobNumber, jobName }) => {
  const { instance, accounts } = useMsal();
  const fileRef = useRef(null);

  const [sources, setSources] = useState(null);
  const [sourceKind, setSourceKind] = useState('sharepoint');
  const [itemId, setItemId] = useState('');
  const [rev, setRev] = useState('');
  const [uploadName, setUploadName] = useState('');
  const [loading, setLoading] = useState(true);
  const [converting, setConverting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3200);
  };

  const loadSources = useCallback(async () => {
    if (!jobNumber) return;
    setLoading(true);
    setError(null);
    try {
      const res = await authenticatedApiCall(instance, accounts, 'GET', `${API(jobNumber)}/sources`);
      const data = res.data?.data || {};
      setSources(data);
      const files = data.sharepoint?.files || [];
      const revs = data.p6?.revisions || [];
      if (files.length) {
        setSourceKind('sharepoint');
        setItemId((prev) => prev || files[0].id);
      } else if (revs.length) {
        setSourceKind('p6');
        setRev((prev) => prev || String(revs[0].projId || revs[0].id || ''));
      } else {
        setSourceKind('upload');
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load sources');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, jobNumber]);

  useEffect(() => {
    loadSources();
  }, [loadSources]);

  const xerFiles = sources?.sharepoint?.files || [];
  const p6Revs = sources?.p6?.revisions || [];
  const meta = sources?.meta || {};

  const canConvert = useMemo(() => {
    if (!jobNumber || converting) return false;
    if (sourceKind === 'sharepoint') return Boolean(itemId);
    if (sourceKind === 'p6') return p6Revs.length > 0;
    if (sourceKind === 'upload') return Boolean(fileRef.current?.files?.[0] || uploadName);
    return false;
  }, [jobNumber, converting, sourceKind, itemId, p6Revs.length, uploadName]);

  const convert = async () => {
    if (!jobNumber) return;
    setConverting(true);
    setError(null);
    setSaved(null);
    try {
      let res;
      if (sourceKind === 'upload') {
        const file = fileRef.current?.files?.[0];
        if (!file) throw new Error('Choose an XER file first');
        const fd = new FormData();
        fd.append('source', 'upload');
        fd.append('file', file);
        res = await authenticatedApiCall(instance, accounts, 'POST', `${API(jobNumber)}/convert`, fd, CONVERT_CFG);
      } else {
        res = await authenticatedApiCall(
          instance,
          accounts,
          'POST',
          `${API(jobNumber)}/convert`,
          sourceKind === 'p6' ? { source: 'p6', rev } : { source: 'sharepoint', itemId },
          CONVERT_CFG
        );
      }
      const data = res.data?.data;
      setResult(data);
      if (data?.fidelity?.ok) showToast('Dates and logic match P6');
      else showToast('Converted — review the mismatches before filing', 'error');
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Convert failed');
    } finally {
      setConverting(false);
    }
  };

  const downloadKind = async (kind) => {
    if (!result?.id) return;
    setDownloading(kind);
    try {
      const res = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        `${API(jobNumber)}/download/${result.id}/${kind}`,
        null,
        { responseType: 'blob', timeout: 180000 }
      );
      const fallback = kind === 'pdf' ? result.pdfName : result.xmlName;
      triggerDownload(res.data, filenameFromHeader(res.headers, fallback));
    } catch (e) {
      showToast(e.response?.data?.error || e.message || 'Download failed', 'error');
    } finally {
      setDownloading(null);
    }
  };

  const saveToSharePoint = async () => {
    if (!result?.id || !result.fidelity?.ok) return;
    setSaving(true);
    try {
      const res = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        `${API(jobNumber)}/save-sharepoint/${result.id}`,
        {},
        { timeout: 180000 }
      );
      setSaved(res.data?.data || null);
      showToast('Saved to the job Schedule library');
    } catch (e) {
      showToast(e.response?.data?.error || e.message || 'SharePoint save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!jobNumber) {
    return <div style={{ padding: 24, color: '#64748b' }}>Open a job to convert its P6 schedule.</div>;
  }

  const fidelity = result?.fidelity;
  const ok = Boolean(fidelity?.ok);

  return (
    <div style={styles.page}>
      <div style={styles.hero}>
        <div style={styles.heroGlow} />
        <div style={styles.heroInner}>
          <div>
            <h1 style={styles.title}>MS Project</h1>
            <p style={styles.subtitle}>
              Convert the P6 schedule for {jobNumber}
              {jobName ? ` — ${jobName}` : ''} into MS Project XML and a letterhead PDF.
              Dates and predecessor logic stay locked to P6.
            </p>
          </div>
          <div style={styles.toolbar}>
            <button type="button" style={btnGhost} onClick={loadSources} disabled={loading}>Refresh</button>
            <button type="button" style={btnGhost} onClick={() => setShowHelp(true)}>❓ Help</button>
          </div>
        </div>
      </div>

      <div style={styles.body}>
        {error && <div style={styles.error}>{error}</div>}

        <section style={styles.card}>
          <h2 style={styles.h2}>1. Source</h2>
          <p style={styles.hint}>
            Prefer the weekly SharePoint XER — that is what went to the client for Novelis-style jobs.
            Live P6 is used only when that revision still has activities.
          </p>
          <div style={styles.radioRow}>
            <label style={styles.radio}>
              <input
                type="radio"
                name="msp-source"
                checked={sourceKind === 'sharepoint'}
                onChange={() => setSourceKind('sharepoint')}
                disabled={!xerFiles.length}
              />
              SharePoint XER {xerFiles.length ? `(${xerFiles.length})` : '(none found)'}
            </label>
            <label style={styles.radio}>
              <input
                type="radio"
                name="msp-source"
                checked={sourceKind === 'p6'}
                onChange={() => setSourceKind('p6')}
                disabled={!p6Revs.length}
              />
              Live P6 {p6Revs.length ? `(${p6Revs.length} rev)` : '(empty)'}
            </label>
            <label style={styles.radio}>
              <input
                type="radio"
                name="msp-source"
                checked={sourceKind === 'upload'}
                onChange={() => setSourceKind('upload')}
              />
              Upload XER
            </label>
          </div>

          {sourceKind === 'sharepoint' && (
            <select
              style={styles.select}
              value={itemId}
              onChange={(e) => setItemId(e.target.value)}
            >
              {xerFiles.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.weekKey || f.dataDate || ''} — {f.name}
                </option>
              ))}
            </select>
          )}
          {sourceKind === 'p6' && (
            <select style={styles.select} value={rev} onChange={(e) => setRev(e.target.value)}>
              {p6Revs.map((r) => (
                <option key={r.projId || r.id} value={r.projId || r.id}>
                  {r.projShortName || r.name} — {r.taskCount} activities
                </option>
              ))}
            </select>
          )}
          {sourceKind === 'upload' && (
            <div>
              <input
                ref={fileRef}
                type="file"
                accept=".xer"
                onChange={(e) => setUploadName(e.target.files?.[0]?.name || '')}
              />
              {uploadName && <span style={styles.fileName}>{uploadName}</span>}
            </div>
          )}

          {sources?.sharepoint?.error && sourceKind === 'sharepoint' && (
            <p style={styles.warn}>{sources.sharepoint.error}</p>
          )}
          {sources?.p6?.reason && sourceKind === 'p6' && (
            <p style={styles.warn}>{sources.p6.reason}</p>
          )}

          <div style={{ marginTop: 14 }}>
            <button type="button" style={btnPrimary} onClick={convert} disabled={!canConvert || converting}>
              {converting ? 'Converting…' : 'Convert'}
            </button>
          </div>
        </section>

        {result && (
          <section style={styles.card}>
            <h2 style={styles.h2}>2. Fidelity</h2>
            <div style={{ ...styles.banner, background: ok ? '#ecfdf5' : '#fef2f2', borderColor: ok ? '#6ee7b7' : '#fecaca' }}>
              <strong style={{ color: ok ? '#047857' : '#b91c1c' }}>
                {ok ? 'Dates and logic match P6.' : 'Mismatch — do not file this pack.'}
              </strong>
              <div style={styles.stats}>
                {result.summary?.activityCount} activities
                {' · '}
                {result.summary?.predecessorCount} relationships
                {' · '}
                data date {result.dataDate || '—'}
              </div>
              <div style={styles.fileStem}>{result.stem}</div>
            </div>
            {!ok && (
              <ul style={styles.mismatch}>
                {fidelity.missingInMsp?.map((code) => (
                  <li key={`miss-${code}`}>Missing in MS Project: {code}</li>
                ))}
                {fidelity.dateMismatches?.map((m, i) => (
                  <li key={`d-${i}`}>{m.taskCode} {m.field}: P6 {String(m.p6)} vs MSP {String(m.msp)}</li>
                ))}
                {fidelity.missingLogic?.map((m, i) => (
                  <li key={`l-${i}`}>{m.predecessor} → {m.successor} {m.type} missing</li>
                ))}
                {fidelity.logicMismatches?.map((m, i) => (
                  <li key={`lm-${i}`}>{m.predecessor} → {m.successor} {m.field}</li>
                ))}
              </ul>
            )}
            {result.preview?.length > 0 && (
              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>ID</th>
                      <th style={styles.th}>Name</th>
                      <th style={styles.th}>Start</th>
                      <th style={styles.th}>Finish</th>
                      <th style={styles.th}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.preview.map((row) => (
                      <tr key={row.taskCode}>
                        <td style={styles.td}>{row.taskCode}</td>
                        <td style={styles.td}>{row.taskName}</td>
                        <td style={styles.td}>{row.start}</td>
                        <td style={styles.td}>{row.finish}</td>
                        <td style={styles.td}>{row.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {result && (
          <section style={styles.card}>
            <h2 style={styles.h2}>3. Deliverables</h2>
            <p style={styles.hint}>
              The MS Project file is XML (the same interchange P6 exports). Open it in MS Project,
              then Save As MPP if the client wants the binary. Dates stay locked — they are
              manually scheduled from P6, not recalculated.
            </p>
            <div style={styles.toolbar}>
              <button
                type="button"
                style={btnPrimary}
                onClick={() => downloadKind('xml')}
                disabled={downloading}
              >
                {downloading === 'xml' ? 'Downloading…' : 'Download MS Project XML'}
              </button>
              <button
                type="button"
                style={btn}
                onClick={() => downloadKind('pdf')}
                disabled={downloading}
              >
                {downloading === 'pdf' ? 'Downloading…' : 'Download letterhead PDF'}
              </button>
              <button
                type="button"
                style={ok ? btnPrimary : btn}
                onClick={saveToSharePoint}
                disabled={!ok || saving}
              >
                {saving ? 'Saving…' : 'Save to SharePoint'}
              </button>
            </div>
            {saved && (
              <p style={styles.saved}>
                Saved to {saved.sitePath} / {saved.driveName} / {saved.folderPath}
                {saved.files?.length ? ` (${saved.files.map((f) => f.name).join(', ')})` : ''}
              </p>
            )}
            <p style={styles.metaLine}>
              Estimate {meta.estimateNumber || '—'} · Job {meta.jobNumber || jobNumber}
              {meta.clientName ? ` · ${meta.clientName}` : ''}
            </p>
          </section>
        )}
      </div>

      {showHelp && (
        <div style={styles.modalBackdrop} onClick={() => setShowHelp(false)}>
          <div style={{ ...styles.modal, maxWidth: 680 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ ...styles.modalHeader, background: ACCENT, color: '#fff' }}>
              <strong style={{ flex: 1 }}>❓ MS Project — Help</strong>
              <ListenButton
                compact
                guideId="schedule-msp-export-help"
                title="MS Project Export Help"
                accent={ACCENT}
                dark
              />
              <button type="button" onClick={() => setShowHelp(false)} style={styles.closeX}>×</button>
            </div>
            <div style={{ padding: 20 }}>
              <HelpWithVideoTour guideId="schedule-msp-export-help" title="MS Project Export Help" accent={ACCENT}>
                <p>
                  <strong>Why this page exists.</strong> Saving a P6 file under an MS Project
                  extension used to shift dates and drop logic. This converter writes the same
                  interchange P6 already uses, then locks every activity so MS Project cannot
                  recalculate bars.
                </p>
                <p>
                  <strong>What you get.</strong> An MS Project XML file and a landscape letterhead
                  PDF. Open the XML in MS Project and Save As MPP if the client wants the binary.
                  File both into the job Schedule library week folder, next to the P6 XER.
                </p>
                <p style={{ marginBottom: 0 }}>
                  Convert only saves to SharePoint when dates and predecessor types (including lag)
                  match P6 exactly.
                </p>
              </HelpWithVideoTour>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div style={{ ...styles.toast, background: toast.type === 'error' ? '#b91c1c' : ACCENT }}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100%',
    background: 'linear-gradient(180deg, #f0fdfa 0%, #f8fafc 180px, #f8fafc 100%)',
    padding: '0 0 40px',
  },
  hero: {
    position: 'relative',
    overflow: 'hidden',
    borderBottom: '1px solid #d1fae5',
    marginBottom: 20,
  },
  heroGlow: {
    position: 'absolute',
    inset: 0,
    background:
      'radial-gradient(ellipse 60% 120% at 10% -20%, rgba(15,118,110,0.18), transparent 55%), radial-gradient(ellipse 40% 80% at 90% 0%, rgba(20,184,166,0.12), transparent 50%)',
    pointerEvents: 'none',
  },
  heroInner: {
    position: 'relative',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
    padding: '20px 24px 18px',
  },
  title: {
    margin: 0,
    fontSize: '1.45rem',
    fontWeight: 700,
    color: '#0f172a',
    letterSpacing: '-0.02em',
  },
  subtitle: {
    margin: '4px 0 0',
    color: '#64748b',
    fontSize: 13,
    maxWidth: 720,
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  body: {
    padding: '0 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    maxWidth: 1100,
  },
  card: {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    padding: 18,
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
  },
  h2: {
    margin: '0 0 8px',
    fontSize: 15,
    color: '#0f172a',
  },
  hint: {
    margin: '0 0 12px',
    color: '#64748b',
    fontSize: 13,
    lineHeight: 1.45,
  },
  radioRow: {
    display: 'flex',
    gap: 16,
    flexWrap: 'wrap',
    marginBottom: 10,
  },
  radio: {
    fontSize: 13,
    color: '#334155',
    display: 'flex',
    gap: 6,
    alignItems: 'center',
  },
  select: {
    width: '100%',
    maxWidth: 720,
    padding: '8px 10px',
    borderRadius: 8,
    border: '1px solid #cbd5e1',
    fontSize: 13,
  },
  fileName: {
    marginLeft: 8,
    fontSize: 13,
    color: '#475569',
  },
  warn: {
    color: '#b45309',
    fontSize: 13,
    marginTop: 8,
  },
  error: {
    background: '#fef2f2',
    color: '#b91c1c',
    border: '1px solid #fecaca',
    borderRadius: 10,
    padding: '10px 14px',
    fontSize: 13,
  },
  banner: {
    border: '1px solid',
    borderRadius: 10,
    padding: '12px 14px',
  },
  stats: {
    marginTop: 6,
    fontSize: 13,
    color: '#334155',
  },
  fileStem: {
    marginTop: 4,
    fontSize: 12,
    color: '#64748b',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  },
  mismatch: {
    margin: '10px 0 0',
    paddingLeft: 18,
    color: '#9f1239',
    fontSize: 12,
  },
  tableWrap: {
    overflowX: 'auto',
    marginTop: 14,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 12,
  },
  th: {
    textAlign: 'left',
    padding: '5px 6px',
    borderBottom: '1px solid #cbd5e1',
    color: '#475569',
    fontWeight: 600,
  },
  td: {
    padding: '5px 6px',
    borderBottom: '1px solid #e2e8f0',
    color: '#334155',
  },
  saved: {
    marginTop: 12,
    color: '#047857',
    fontSize: 13,
  },
  metaLine: {
    margin: '10px 0 0',
    fontSize: 12,
    color: '#94a3b8',
  },
  modalBackdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.45)',
    zIndex: 80,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  modal: {
    background: '#fff',
    borderRadius: 12,
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 16px',
  },
  closeX: {
    border: 'none',
    background: 'transparent',
    color: '#fff',
    fontSize: 22,
    cursor: 'pointer',
    lineHeight: 1,
  },
  toast: {
    position: 'fixed',
    bottom: 24,
    right: 24,
    color: '#fff',
    padding: '10px 14px',
    borderRadius: 8,
    fontSize: 13,
    zIndex: 90,
  },
};

export default MspExport;
